import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FileText,
  Download,
  Loader2,
  AlertCircle,
  RefreshCw,
  CalendarDays,
} from "lucide-react";
import {
  getBusinessInvoicePaidMonths,
  downloadBusinessInvoice,
} from "../../store/slices/businessOwnerSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function BusinessInvoices() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    invoicePaidMonths,
    invoiceLoading,
    invoiceError,
    invoiceDownloadingMap,
  } = useSelector((state) => state.businessOwner);

  const businessUuid = user?.business_id || "";

  // ── fetch paid months ──────────────────────────────────────────────────────
  const fetchPaidMonths = useCallback(() => {
    dispatch(getBusinessInvoicePaidMonths());
  }, [dispatch]);

  useEffect(() => {
    fetchPaidMonths();
  }, [fetchPaidMonths]);

  // ── download invoice ───────────────────────────────────────────────────────
  const handleDownload = useCallback(async (month, year, monthName) => {
    if (!businessUuid) {
      showApiError(null, "Business not found");
      return;
    }
    const result = await dispatch(downloadBusinessInvoice({ businessUuid, month, year, monthName }));
    if (downloadBusinessInvoice.fulfilled.match(result)) {
      // Trigger browser download
      const { blobUrl, filename } = result.payload;
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      showApiSuccess("Invoice downloaded successfully");
    } else {
      showApiError(null, result.payload || "Failed to download invoice");
    }
  }, [dispatch, businessUuid]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <FileText className="h-6 w-6 sm:h-7 sm:w-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Invoices</h1>
              <p className="text-xs sm:text-sm text-gray-500">Download commission invoices for paid months</p>
            </div>
          </div>
          <button
            onClick={fetchPaidMonths}
            disabled={invoiceLoading}
            className="self-end sm:self-auto p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${invoiceLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {invoiceLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            <span>Loading invoices…</span>
          </div>
        ) : invoiceError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">{invoiceError}</p>
            <button onClick={fetchPaidMonths} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors">
              Try Again
            </button>
          </div>
        ) : invoicePaidMonths.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FileText className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">No invoices available</p>
            <p className="text-gray-400 text-sm">Invoices will appear here once commission payments are settled.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Period", "Paid At", "Action"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {invoicePaidMonths.map((item, idx) => {
                  const key = `${item.month}-${item.year}`;
                  const isDownloading = invoiceDownloadingMap[key] || false;

                  return (
                    <tr key={key} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CalendarDays className="h-4 w-4 text-purple-500" />
                          <span className="font-semibold text-gray-900">{item.formatted || `${item.month_name} ${item.year}`}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(item.paid_at)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDownload(item.month, item.year, item.month_name)}
                          disabled={isDownloading}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors disabled:opacity-50"
                        >
                          {isDownloading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                          {isDownloading ? "Downloading…" : "Download Invoice"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
